import { Controller } from '@hotwired/stimulus'

// Keeps the card's change preview in step with the group selectors. Tagify
// writes its state back to the underlying input as JSON and fires `change`, so
// that event is the signal rather than anything tagify-specific.
export default class extends Controller {
  static targets = ['frame', 'devicesFrame', 'includedInput', 'excludedInput']
  static values = {
    url: String,
    debounce: { type: Number, default: 300 },
  }

  connect() {
    this.selectors().forEach((input) => input.addEventListener('change', this.schedule))
  }

  disconnect() {
    clearTimeout(this.timeout)
    this.selectors().forEach((input) => input.removeEventListener('change', this.schedule))
  }

  // Each reload costs two fleet resolutions server-side, so coalesce the bursts
  // tagify emits while a group is being picked.
  schedule = () => {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(this.reload, this.debounceValue)
  }

  // Turbo refetches a frame when its src changes, so an edit that lands back on
  // the current selection is a no-op rather than a duplicate request.
  reload = () => {
    if (!this.hasFrameTarget) {
      return
    }

    const url = new URL(this.urlValue, window.location.origin)
    this.appendIds(url, 'included_group_ids[]', this.includedInputTarget)
    this.appendIds(url, 'excluded_group_ids[]', this.excludedInputTarget)

    // The open list describes the edit that produced it, so it is stale the
    // moment the selection moves on. Close it rather than show stale rows.
    this.hideDevices()
    this.frameTarget.src = url.toString()
  }

  hideDevices() {
    if (!this.hasDevicesFrameTarget) {
      return
    }

    this.devicesFrameTarget.removeAttribute('src')
    this.devicesFrameTarget.innerHTML = ''
  }

  appendIds(url, param, input) {
    this.selectedIds(input).forEach((id) => url.searchParams.append(param, id))
  }

  selectedIds(input) {
    if (!input?.value) {
      return []
    }

    try {
      return JSON.parse(input.value).map((tag) => String(tag.value))
    } catch {
      return []
    }
  }

  selectors() {
    return [
      this.hasIncludedInputTarget ? this.includedInputTarget : null,
      this.hasExcludedInputTarget ? this.excludedInputTarget : null,
    ].filter(Boolean)
  }
}
